import urlsService from '@services/urls-service';
import { baseApi } from '@state/baseApi';
import { setCookie, deleteCookie } from '@utils/cookies';
import type * as Types from './quotesTypes';

const CACHE_TAG = 'QUOTES' as const;
export const QUOTES_KEY_COOKIE = 'cl_quotesKey';

export const quotesApi = baseApi.enhanceEndpoints({ addTagTypes: [CACHE_TAG] }).injectEndpoints({
  endpoints: (builder) => ({
    getQuote: builder.query<Types.QuoteResponse, Types.GetQuoteRequest>({
      query: ({ cartKey }) => ({
        url: urlsService.getUrl(`/quotes/${cartKey}`),
        method: 'GET'
      })
    }),
    upsertItem: builder.mutation<Types.QuoteResponse, Types.AddToQuoteRequest>({
      query: ({ cartKey, variantCode, unitOfMeasureCode, quantity = 1, reference }) => ({
        url: urlsService.getUrl('/quotes/additem'),
        method: 'POST',
        body: {
          cartKey,
          lines: [{
            variantCode,
            unitOfMeasureCode,
            quantity,
            reference
          }]
        }
      }),
      async onQueryStarted({ cartKey, variantCode, unitOfMeasureCode, quantity = 1, reference }, { dispatch, queryFulfilled }) {
        const isUpdatingExistingQuote = !!cartKey;

        // Apply optimistic update only when updating an existing quote
        const patchResult = isUpdatingExistingQuote
          ? dispatch(
            quotesApi.util.updateQueryData('getQuote', { cartKey }, (draft) => {
              const existingLine = draft.lines.find(
                line => line.variantCode === variantCode && line.unitOfMeasureCode === unitOfMeasureCode
              );
              if (existingLine) {
                existingLine.quantity += quantity;
                existingLine.reference = reference;
              }
              // Note: Cannot add new line optimistically without full product data
            })
          )
          : null;

        try {
          const { data } = await queryFulfilled;

          if (data.cartKey) {
            setCookie(QUOTES_KEY_COOKIE, data.cartKey);

            // Cache the newly created quote
            if (!isUpdatingExistingQuote) {
              dispatch(
                quotesApi.util.upsertQueryData('getQuote', { cartKey: data.cartKey }, data)
              );
            }
          }
        } catch {
          patchResult?.undo();
        }
      }
    }),
    updateQuote: builder.mutation<Types.QuoteResponse, Types.UpdateQuoteRequest>({
      query: (quoteData) => ({
        url: urlsService.getUrl('/quotes/update'),
        method: 'POST',
        body: quoteData
      }),
      async onQueryStarted(quoteData, { dispatch, queryFulfilled }) {
        // Only apply optimistic update if cartKey exists
        const patchResult = quoteData.cartKey ? dispatch(
          quotesApi.util.updateQueryData('getQuote', { cartKey: quoteData.cartKey }, (draft) => {
            Object.assign(draft, quoteData);
          })
        ) : null;
        try {
          await queryFulfilled;
        } catch {
          patchResult?.undo();
        }
      }
    }),
    removeFromQuote: builder.mutation<Types.QuoteResponse, Types.RemoveFromQuoteRequest>({
      query: ({ cartKey, variantCode }) => ({
        url: urlsService.getUrl('/quotes/deleteitem'),
        method: 'POST',
        body: {
          cartKey,
          variantCode
        }
      }),
      async onQueryStarted({ cartKey, variantCode }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          quotesApi.util.updateQueryData('getQuote', { cartKey }, (draft) => {
            draft.lines = draft.lines.filter(line => line.variantCode !== variantCode);
          })
        );
        try {
          const { data } = await queryFulfilled;
          if (data.cartKey) {
            setCookie(QUOTES_KEY_COOKIE, data.cartKey);
          }
        } catch {
          patchResult.undo();
        }
      }
    }),
    confirmQuote: builder.mutation<Types.ConfirmQuoteResponse, Types.ConfirmQuoteRequest>({
      query: ({ cartKey, orderReference }) => ({
        url: urlsService.getUrl('/quotes/confirm'),
        method: 'POST',
        body: {
          cartKey,
          orderReference
        }
      }),
      async onQueryStarted({ cartKey }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          quotesApi.util.updateQueryData('getQuote', { cartKey }, (draft) => {
            draft.status = 'status_confirmed_by_client';
          })
        );
        try {
          await queryFulfilled;
          deleteCookie(QUOTES_KEY_COOKIE);
        } catch {
          patchResult.undo();
        }
      }
    })
  }),
  overrideExisting: true
});

export const {
  useGetQuoteQuery,
  useLazyGetQuoteQuery,
  useUpsertItemMutation,
  useRemoveFromQuoteMutation,
  useUpdateQuoteMutation,
  useConfirmQuoteMutation
} = quotesApi;


const quoteSelector = ({ cartKey }: { cartKey: string }) => quotesApi.endpoints.getQuote.select({ cartKey });

export const selectQuoteByCartKey = (cartKey: string) => (state: any) => {
  const selectQuote = quoteSelector({ cartKey });
  const quoteResult = selectQuote(state);
  return quoteResult.data;
};
